import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, Palette, Paintbrush } from "lucide-react";
import { cn } from "@/lib/utils";

const PREDEFINED_COLORS = [
  { value: "#d7fcf0", label: "Verde Menta" },
  { value: "#b9fee8", label: "Verde Agua" },
  { value: "#f79dad", label: "Rosa Claro" },
  { value: "#ffcfd7", label: "Rosa Pastel" },
  { value: "#bdb3ff", label: "Púrpura Claro" },
  { value: "#dcd7fc", label: "Lavanda" },
];

interface EnhancedColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

export function EnhancedColorPicker({ value, onChange, label = "Color" }: EnhancedColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sincronizar customColor cuando value cambie desde afuera
  useEffect(() => {
    setCustomColor(value);
  }, [value]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handlePredefinedColorSelect = (color: string) => {
    onChange(color);
    setCustomColor(color);
    // Mantener el popover abierto para permitir selección múltiple
  };

  const handleCustomColorChange = (color: string) => {
    setCustomColor(color);
    onChange(color);
  };

  const isPredefinedColor = PREDEFINED_COLORS.some(color => color.value === value);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        {/* Color Display */}
        <div className="flex-1">
          <Input
            type="color"
            value={value}
            onChange={(e) => handleCustomColorChange(e.target.value)}
            className="h-10 w-full"
          />
        </div>
        
        {/* Predefined Colors Button */}
        <div className="relative" ref={dropdownRef}>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10"
            title="Colores predeterminados"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Palette className="h-4 w-4" />
          </Button>
          
          {/* Custom Dropdown */}
          {isOpen && (
            <div className="absolute top-full left-0 mt-2 w-80 p-4 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Paintbrush className="h-4 w-4" />
                  <h4 className="font-medium">Colores Predeterminados</h4>
                </div>
                
                {/* Predefined Colors Grid */}
                <div className="grid grid-cols-3 gap-2">
                  {PREDEFINED_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={cn(
                        "w-12 h-12 rounded-md border-2 transition-all relative group",
                        value === color.value
                          ? "border-primary scale-110 shadow-md"
                          : "border-gray-300 hover:scale-105 hover:shadow-sm"
                      )}
                      style={{ backgroundColor: color.value }}
                      onClick={() => handlePredefinedColorSelect(color.value)}
                      title={color.label}
                    >
                      {value === color.value && (
                        <Check className="h-5 w-5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-800" />
                      )}
                      
                      {/* Tooltip on hover */}
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                        {color.label}
                      </div>
                    </button>
                  ))}
                </div>
                
                {/* Current Color Info */}
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div 
                      className="w-4 h-4 rounded border"
                      style={{ backgroundColor: value }}
                    />
                    <span>Color actual: {value.toUpperCase()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
